import { CAPTURE_TYPES } from "./Capture.helpers";
import { LINK_KIND, captureSourceUrl, linkHost, linkKind } from "./captureLinks";

/**
 * captureToBlock — the ONE place that decides which Segment block a captured
 * item becomes.
 *
 * WHY THIS EXISTS (Kelly, VP Sales, QA-52/QA-53, reported three times):
 *
 *   "if I'm going to drag over a video to a segment it should recognize it's a
 *    video and not make me bring over a video tool just to drop the video
 *    capture in it."
 *
 * Until now the ONLY route from a capture into a Segment was: drag an EMPTY
 * media block in first, open its BASIC tab, "Reuse from your Media Library",
 * Captures tab, pick the item. Seven steps, none of them signposted. The block
 * type was something the USER had to know and choose up front.
 *
 * This module inverts that: the capture's own `type` decides the block, so the
 * user never picks a tool. It is deliberately PURE — no craft.js, no React, no
 * redux — so the mapping can be asserted directly, one behaviour per test, and
 * a failure names the capture type that broke rather than sending anyone into a
 * component render log.
 *
 * The craft.js widget NAMES below are the resolver keys registered in
 * `src/pages/SegmentBuilder/Editor.jsx`. They are strings here on purpose: this
 * module must stay importable by a plain unit test with no craft Editor mounted.
 */

/** Escape text for safe interpolation into an HTML attribute or text node. */
const escapeHtmlText = (value) =>
  String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

/** craft.js resolver keys — must match `pages/SegmentBuilder/Editor.jsx`. */
export const BLOCK_WIDGETS = {
  IMAGE: "ImageWidget",
  VIDEO: "VideoWidget",
  AUDIO: "AudioWidget",
  DOCUMENT: "DocumentWidget",
  TEXT: "TextWidget",
};

/**
 * capture type -> the block a user would have had to drag in by hand.
 * PHOTO is an Image block; a text-only Note becomes a Text block (it has no
 * media_key at all, so it can never be media — see isCaptureInsertable).
 */
const WIDGET_BY_CAPTURE_TYPE = {
  [CAPTURE_TYPES.PHOTO]: BLOCK_WIDGETS.IMAGE,
  [CAPTURE_TYPES.VIDEO]: BLOCK_WIDGETS.VIDEO,
  [CAPTURE_TYPES.AUDIO]: BLOCK_WIDGETS.AUDIO,
  [CAPTURE_TYPES.DOC]: BLOCK_WIDGETS.DOCUMENT,
  [CAPTURE_TYPES.TEXT]: BLOCK_WIDGETS.TEXT,
};

/**
 * Which block does this capture become?
 * Returns null for an unknown/absent type rather than guessing a default — a
 * silently-wrong block type is exactly the class of bug this feature exists to
 * remove, so an unknown type must be REFUSED (with a message) not approximated.
 */
export const blockWidgetForCapture = (item) => {
  if (!item || !item.type) return null;
  if (item.type === CAPTURE_TYPES.LINK) {
    /**
     * QA-98: "Support Embed URLs for Videos and Images, to match what the Wheel
     * Builder blocks can already do." A captured link therefore becomes the
     * block its DESTINATION implies, exactly as an uploaded capture becomes the
     * block its file type implies — a YouTube share URL is a Video block, a
     * direct image URL is an Image block, and anything else is a Text block
     * carrying the link.
     *
     * The alternative — every link becoming a Text block — would be the same
     * defect Kelly reported for media captures: the user knows what they
     * dropped, and having to swap the block by hand afterwards is the papercut
     * this whole mechanism exists to remove.
     */
    const kind = linkKind(captureSourceUrl(item));
    if (kind === LINK_KIND.VIDEO) return BLOCK_WIDGETS.VIDEO;
    if (kind === LINK_KIND.IMAGE) return BLOCK_WIDGETS.IMAGE;
    return BLOCK_WIDGETS.TEXT;
  }
  return WIDGET_BY_CAPTURE_TYPE[item.type] || null;
};

/**
 * A media capture with no resolvable URL cannot become a media block: the block
 * would render an empty frame, which reads to the user as "it didn't work" —
 * the same silence that caused three duplicate reports. Refuse it instead.
 *
 * A TEXT capture is insertable on its text_content alone (it has no media).
 */
export const isCaptureInsertable = (item) => {
  const widget = blockWidgetForCapture(item);
  if (!widget) return false;
  // QA-98: a LINK carries its payload in source_url, never in media_url, so its
  // insertability turns on the URL being present and valid — including for the
  // page case, which becomes a Text block holding the link rather than an
  // empty note.
  if (item && item.type === CAPTURE_TYPES.LINK) {
    return !!captureSourceUrl(item);
  }
  if (widget === BLOCK_WIDGETS.TEXT) {
    return !!(item.text_content && String(item.text_content).trim());
  }
  return !!item.media_url;
};

/**
 * Why a capture cannot be inserted, in the user's words. Rendered as the drop
 * REJECTION message — Kelly's recorded attempt produced complete silence, and
 * silence reads as broken. Returns null when the item IS insertable.
 */
export const captureRejectionReason = (item) => {
  if (isCaptureInsertable(item)) return null;
  if (item && item.type === CAPTURE_TYPES.LINK) {
    return "This link is missing or can't be opened, so there's nothing to add.";
  }
  if (!blockWidgetForCapture(item)) {
    return "This capture type can't be added to a Segment yet.";
  }
  if (blockWidgetForCapture(item) === BLOCK_WIDGETS.TEXT) {
    return "This note is empty, so there's nothing to add.";
  }
  return "This capture is still uploading, or its file is missing.";
};

/**
 * The props the new block is created with. Kept separate from the widget name
 * so each can be asserted on its own and a failure localises to one or the
 * other.
 *
 * `title` falls back to the capture type label rather than being left as the
 * widget's own "Untitled Video" default, so the block a user just added is
 * identifiable in the segment without opening it.
 */
export const captureBlockProps = (item) => {
  const widget = blockWidgetForCapture(item);
  if (!widget) return null;

  const note = item.text_content ? String(item.text_content).trim() : "";
  // The capture's own Title field (set in the New/Edit Capture modal) — the
  // same field the TEXT case already prefers over its note. PHOTO/VIDEO/
  // AUDIO/DOCUMENT never read it below this point (slack-devs-74f79f item 4):
  // the block a user just inserted always showed as generic "Captured photo"/
  // "Uploaded image" even when the capture had a real, user-given Title,
  // making same-type blocks in a segment indistinguishable from each other.
  const itemTitle = typeof item.title === "string" ? item.title.trim() : "";

  // QA-98 + QA-99. A LINK block is built from the URL, and the URL travels WITH
  // it: an embedded YouTube video that cannot say where it came from is exactly
  // the provenance loss QA-99 exists to prevent.
  if (item.type === CAPTURE_TYPES.LINK) {
    const url = captureSourceUrl(item);
    const host = linkHost(url);
    if (widget === BLOCK_WIDGETS.VIDEO) {
      return {
        src: url,
        embed: url,
        sourceUrl: url,
        alt: note || `Video from ${host || "a link"}`,
        title: note || `Video from ${host || "a link"}`,
      };
    }
    if (widget === BLOCK_WIDGETS.IMAGE) {
      return {
        src: url,
        sourceUrl: url,
        alt: note || `Image from ${host || "a link"}`,
      };
    }
    // A page: a Text block that still CONTAINS the link, rather than a note
    // whose only content is a title the user then has to go and find again.
    //
    // QA-121: `text` renders through FroalaEditorView (an HTML view, not a
    // plain-text one — see components/SegmentBuilder/CodeBlocks/Text/Text.jsx)
    // — a bare url string with no <a> tag just paints as inert text, which is
    // exactly Kelly's report ("must render as a real clickable HYPERLINK,
    // currently plain text"). Build real anchor markup instead. Escaped so a
    // url/note containing `<`, `&`, or `"` cannot break out of the href
    // attribute or inject markup — the source_url itself is already
    // scheme-restricted to http(s) server-side (capture/api/serializers.py
    // _validate_source_url), but its path/query is not otherwise restricted.
    const escapedUrl = escapeHtmlText(url);
    const linkHtml = `<p><a href="${escapedUrl}" target="_blank" rel="noopener noreferrer">${escapedUrl}</a></p>`;
    return {
      text: note ? `<p>${escapeHtmlText(note)}</p>${linkHtml}` : linkHtml,
      sourceUrl: url,
    };
  }

  if (widget === BLOCK_WIDGETS.TEXT) {
    // The capture's own Title (`name`, the "Title" field in the New capture
    // modal — distinct from `text_content`/`note`, the Notes field) used to
    // be discarded here, unlike every other widget above which falls back to
    // a title so "the block a user just added is identifiable in the segment
    // without opening it" (see the module doc comment). Prepend it as a
    // heading so a titled note keeps that identity once it's a Segment block.
    const title = item.name ? String(item.name).trim() : "";
    if (!title) return { text: note };
    const heading = `<p><strong>${escapeHtmlText(title)}</strong></p>`;
    return { text: note ? `${heading}${note}` : heading };
  }

  const base = { src: item.media_url };

  if (widget === BLOCK_WIDGETS.IMAGE) {
    // An image with no alt text is a picture nobody can read. The capture's own
    // Title, then its note, are the only human-written descriptions available;
    // fall back to a truthful generic rather than an empty string. `title` is
    // set the same way so the block's edit-mode label (BlockEditorPane's
    // `<UpName>{v.title || "Uploaded image"}</UpName>`) shows the capture's own
    // name instead of a generic placeholder every same-type block shares.
    return { ...base, alt: itemTitle || note || "Captured photo", title: itemTitle };
  }
  if (widget === BLOCK_WIDGETS.VIDEO) {
    return {
      ...base,
      alt: itemTitle || note || "Captured video",
      title: itemTitle || note || "Captured video",
    };
  }
  if (widget === BLOCK_WIDGETS.AUDIO) {
    return { ...base, title: itemTitle || note || "Captured audio" };
  }
  return { ...base, title: itemTitle || note || "Captured document" };
};

/**
 * The full insert descriptor: what to build, and where. `position: "bottom"` is
 * not decoration — it is Kelly's explicit, non-negotiable requirement:
 *
 *   "it should drop at the very bottom of the segment if there is already
 *    information in that segment."
 *
 * It is carried in the descriptor (rather than assumed by the caller) so a test
 * can assert the intent independently of craft.js behaviour.
 */
export const captureInsertDescriptor = (item) => {
  if (!isCaptureInsertable(item)) return null;
  return {
    widget: blockWidgetForCapture(item),
    props: captureBlockProps(item),
    position: "bottom",
  };
};
